'use client';

import React from 'react';
import { toast } from 'sonner';
import {
  Archive,
  Calendar,
  Download,
  Eye,
  FileText,
  Lock,
  MapPin,
  Pencil,
  ShieldCheck,
  FileCheck,
  UserPlus,
  X,
} from 'lucide-react';
import {
  TRUST_LEVEL_COPY,
  trustLevelIndex,
  trustLevelOrder,
  type TrustRecord,
  type TrustVerificationLevel,
} from '@/lib/trust-records';

type TrustRecordViewerRole = 'public' | 'approved' | 'owner' | 'admin';

function formatDate(value?: string) {
  if (!value) return 'Not specified';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not specified';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function TrustBadge({
  level,
  compact = false,
  className = '',
}: {
  level: TrustVerificationLevel;
  compact?: boolean;
  className?: string;
}) {
  const copy = TRUST_LEVEL_COPY[level];
  const Icon = copy.Icon;
  const isLedger = level === 'ledger_verified';

  return (
    <span
      title={copy.tooltip}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold leading-none shadow-sm ${copy.badgeClassName} ${className}`}
      style={
        isLedger
          ? {
              background:
                'linear-gradient(120deg,rgba(255,255,255,0.92),rgba(199,240,255,0.82),rgba(238,221,255,0.9),rgba(255,238,198,0.78))',
              boxShadow: '0 8px 24px rgba(20,184,166,0.16)',
            }
          : undefined
      }
    >
      <Icon size={compact ? 11 : 12} />
      {!compact && copy.label}
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200/70 bg-white/72 p-4 shadow-sm">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">{title}</h3>
      {children}
    </section>
  );
}

function MetadataRow({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <span className="max-w-[58%] text-right text-xs font-semibold text-slate-800">
        {value || 'Not specified'}
      </span>
    </div>
  );
}

function SupportingSummary({ record }: { record: TrustRecord }) {
  const items = [
    record.media_count > 0 &&
      `${record.media_count} ${record.media_count === 1 ? 'media item' : 'media items'}`,
    record.document_count > 0 &&
      `${record.document_count} ${record.document_count === 1 ? 'document' : 'documents'}`,
    record.certificate_count > 0 &&
      `${record.certificate_count} ${record.certificate_count === 1 ? 'certificate' : 'certificates'}`,
    record.community_confirmation_count > 0 &&
      `${record.community_confirmation_count} community ${record.community_confirmation_count === 1 ? 'confirmation' : 'confirmations'}`,
    record.institution_recognition_count > 0 &&
      `${record.institution_recognition_count} institution ${record.institution_recognition_count === 1 ? 'recognition' : 'recognitions'}`,
  ].filter(Boolean);

  if (items.length === 0) {
    return <p className="text-sm text-slate-500">No Trust Record details yet.</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span
          key={String(item)}
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700"
        >
          <FileText size={12} />
          {item}
        </span>
      ))}
    </div>
  );
}

function TrustProgress({ level }: { level: TrustVerificationLevel }) {
  const activeIndex = trustLevelIndex(level);
  return (
    <div className="grid grid-cols-5 gap-1">
      {trustLevelOrder.map((trustLevel, index) => {
        const copy = TRUST_LEVEL_COPY[trustLevel];
        const active = index <= activeIndex;
        return (
          <div
            key={trustLevel}
            title={copy.label}
            className="h-1.5 rounded-full"
            style={{ background: active ? copy.dotColor : 'rgba(148,163,184,0.24)' }}
          />
        );
      })}
    </div>
  );
}

export function TrustRecordDrawer({
  record,
  open,
  onClose,
  viewerRole = 'public',
  journeyContext,
}: {
  record: TrustRecord | null;
  open: boolean;
  onClose: () => void;
  viewerRole?: TrustRecordViewerRole;
  journeyContext?: { source_type?: string; chapter_or_era?: string; source_id?: string };
}) {
  if (!open || !record) return null;

  const copy = TRUST_LEVEL_COPY[record.verification_level];
  const showOwnerActions = viewerRole === 'owner' || viewerRole === 'admin';
  const canViewShared = viewerRole === 'approved' || showOwnerActions;

  const viewSourceLabel = (() => {
    const t = journeyContext?.source_type;
    if (t === 'story') return 'View Story';
    if (t === 'life_card') return 'View Life Card';
    if (t === 'achievement') return 'View Achievement';
    if (t === 'heritage' || t === 'family_record' || t === 'family_ledger')
      return 'View Family Record';
    return 'View Moment';
  })();

  const actions = [
    { label: viewSourceLabel, Icon: Eye, show: true },
    { label: 'View Public Certificate', Icon: ShieldCheck, show: record.certificate_count > 0 },
    { label: 'View Public Media', Icon: Archive, show: record.public_files_enabled },
    { label: 'Request Access', Icon: Lock, show: !canViewShared && record.private_files_enabled },
    { label: 'Add Recognition', Icon: UserPlus, show: viewerRole !== 'public' },
    { label: 'Verify Record', Icon: ShieldCheck, show: viewerRole === 'admin' },
    { label: 'Edit Trust Record', Icon: Pencil, show: showOwnerActions },
    { label: 'Download Summary', Icon: Download, show: showOwnerActions },
  ].filter((action) => action.show);

  return (
    <div className="fixed inset-0 z-[120]">
      <button
        aria-label="Close Trust Record"
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside
        className="trust-record-sheet absolute inset-x-0 bottom-0 overflow-hidden rounded-t-[28px] border border-white/60 bg-white shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:h-full sm:max-h-none sm:w-[480px] sm:rounded-none sm:border-l"
        style={{
          background:
            'linear-gradient(135deg,rgba(255,255,255,0.96),rgba(243,250,255,0.94),rgba(254,247,255,0.95))',
        }}
      >
        <div className="flex h-full flex-col">
          <header className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/82 px-5 py-4 backdrop-blur-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-lg font-bold text-slate-950">Trust Record</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">
                  A transparent record of the context, recognition, and documentation behind this
                  moment.
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition-transform duration-150 active:scale-95"
              >
                <X size={16} />
              </button>
            </div>
          </header>

          <div className="trust-record-scroll flex-1 space-y-4 overflow-y-auto px-5 py-5">
            <div
              className="rounded-3xl border border-white/80 p-5 shadow-sm"
              style={{
                background:
                  'radial-gradient(circle at 0% 0%,rgba(45,212,191,0.18),transparent 34%), radial-gradient(circle at 100% 12%,rgba(168,85,247,0.18),transparent 36%), linear-gradient(135deg,rgba(255,255,255,0.92),rgba(248,250,252,0.82))',
              }}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    Verification Status
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-slate-950">{record.title}</h2>
                </div>
                <TrustBadge level={record.verification_level} />
              </div>
              <p className="mb-4 text-sm leading-relaxed text-slate-600">{copy.meaning}</p>
              <TrustProgress level={record.verification_level} />
            </div>

            <Section title="Verified By or Recognized By">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-white">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{record.verified_by_name}</p>
                  <p className="text-xs capitalize text-slate-500">
                    {record.verified_by_type.replace('_', ' ')}
                  </p>
                </div>
              </div>
            </Section>

            <Section title="Supporting Materials">
              <SupportingSummary record={record} />
              {!record.public_files_enabled && record.private_files_enabled && (
                <p className="mt-3 text-xs text-slate-500">
                  Some materials are private and require owner approval.
                </p>
              )}
            </Section>

            <Section title="Metadata">
              <MetadataRow label="Event date" value={formatDate(record.event_date)} />
              <MetadataRow label="Date added to Ertzyx" value={formatDate(record.created_at)} />
              <MetadataRow
                label="Location"
                value={
                  record.location_name ? (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={11} />
                      {record.location_name}
                    </span>
                  ) : undefined
                }
              />
              <MetadataRow label="Category" value={record.category} />
              <MetadataRow label="Pillar" value={record.pillar} />
              <MetadataRow label="Record ID" value={record.record_id_public} />
              <MetadataRow label="Last updated" value={formatDate(record.updated_at)} />
              <MetadataRow label="Visibility" value={record.visibility} />
              {journeyContext?.source_type && (
                <MetadataRow
                  label="Source module"
                  value={(() => {
                    const sourceLabels: Record<string, string> = {
                      manual_journey: 'Journey Map',
                      story: 'Story Hub',
                      achievement: 'Achievements',
                      credential: 'Credentials',
                      heritage: 'Family Ledger',
                      life_card: 'Life Cards',
                      family_record: 'Family Ledger',
                      family_ledger: 'Family Ledger',
                      oral_history: 'Oral History',
                      collection: 'Collections',
                      career_legacy: 'Career Legacy',
                      community_heritage: 'Community Heritage',
                      institution_verified: 'Institution Record',
                    };
                    return sourceLabels[journeyContext.source_type!] || journeyContext.source_type;
                  })()}
                />
              )}
              {journeyContext?.chapter_or_era && (
                <MetadataRow label="Chapter" value={journeyContext.chapter_or_era} />
              )}
            </Section>

            <Section title="Recognition">
              <div className="space-y-2 text-sm text-slate-700">
                {record.community_confirmation_count > 0 && (
                  <p>Confirmed by {record.community_confirmation_count} people</p>
                )}
                {record.institution_recognition_count > 0 && (
                  <p>Recognized by {record.institution_recognition_count} institution</p>
                )}
                {record.linked_achievement_ids.length > 0 && <p>Linked to Achievement badge</p>}
                {record.linked_journey_item_ids.length > 0 && <p>Added to Journey Map</p>}
                {record.record_type === 'institution_record' && <p>Added to Heritage Journey</p>}
                {record.community_confirmation_count === 0 &&
                  record.institution_recognition_count === 0 &&
                  record.linked_achievement_ids.length === 0 && (
                    <p className="text-slate-500">
                      Add media, metadata, or recognition to strengthen this record.
                    </p>
                  )}
              </div>
            </Section>

            <div className="rounded-2xl border border-cyan-200/70 bg-cyan-50/80 p-4 text-sm text-cyan-950">
              <div className="flex items-start gap-2">
                <Lock size={16} className="mt-0.5 flex-shrink-0" />
                <p>Private documents are protected. Only approved details are visible.</p>
              </div>
            </div>

            {record.audit_history.length > 0 && (
              <Section title="Ledger Activity">
                <div className="space-y-3">
                  {record.audit_history.slice(0, 4).map((event) => (
                    <div key={`${event.label}-${event.at}`} className="flex items-start gap-3">
                      <span className="mt-1 h-2 w-2 rounded-full bg-teal-400" />
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{event.label}</p>
                        <p className="text-xs text-slate-500">
                          {formatDate(event.at)}
                          {event.actor ? ` by ${event.actor}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>

          <footer className="trust-record-footer flex-shrink-0 border-t border-slate-200/70 bg-white/88 p-4 backdrop-blur-xl">
            <div className="grid grid-cols-2 gap-2">
              {actions.map(({ label, Icon }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    if (label === 'Download Summary') {
                      const text = `${record.title}\n\n${copy.meaning}\n\nEvent date: ${formatDate(record.event_date)}\nRecord: ${record.record_id_public || '—'}`;
                      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `ertzyx-trust-${record.record_id_public || 'summary'}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Summary downloaded');
                      return;
                    }
                    if (label === viewSourceLabel) {
                      toast.message(viewSourceLabel, {
                        description: 'Open the linked record from your vault.',
                      });
                      return;
                    }
                    if (label === 'View Public Media') {
                      toast.message('Public Media', {
                        description:
                          'Media files are viewable on the public record page for this trust record.',
                      });
                      return;
                    }
                    if (label === 'View Public Certificate') {
                      toast.message('Certificate', {
                        description:
                          'The institution-issued certificate is linked to this verified record.',
                      });
                      return;
                    }
                    if (label === 'Add Recognition') {
                      toast.info('Add Recognition', {
                        description:
                          'Open your vault or journey panel to add a community or institutional recognition.',
                      });
                      return;
                    }
                    if (label === 'Edit Trust Record') {
                      toast.info('Edit Trust Record', {
                        description:
                          'Open your journey or vault settings to update metadata, privacy, and context for this record.',
                      });
                      return;
                    }
                    if (label === 'Request Access') {
                      toast.info('Request Sent', {
                        description: 'The owner will be notified of your access request.',
                      });
                      return;
                    }
                    if (label === 'Verify Record') {
                      toast.info('Verify Record', {
                        description:
                          'Navigate to your Verification Inbox to issue a formal verification for this record.',
                      });
                      return;
                    }
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-sm transition-transform duration-150 active:scale-95 hover:bg-slate-50"
                  title={
                    label === viewSourceLabel
                      ? 'Open the linked record from your vault'
                      : label === 'View Public Certificate'
                        ? 'Open the institution-issued certificate'
                        : label === 'View Public Media'
                          ? 'Browse publicly shared media for this record'
                          : label === 'Request Access'
                            ? 'Ask the owner for access to private documents'
                            : label === 'Add Recognition'
                              ? 'Add a community or institutional recognition to strengthen this record'
                              : label === 'Verify Record'
                                ? 'Issue an official institution verification'
                                : label === 'Edit Trust Record'
                                  ? 'Edit the context, metadata, and privacy settings'
                                  : label === 'Download Summary'
                                    ? 'Download a text summary of this trust record'
                                    : label
                  }
                >
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </div>
          </footer>
        </div>
      </aside>
    </div>
  );
}

export function TrustRecordInlineSummary({ record }: { record: TrustRecord }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
      <span className="inline-flex items-center gap-1">
        <Calendar size={12} />
        {formatDate(record.event_date)}
      </span>
      {record.location_name && (
        <span className="inline-flex items-center gap-1">
          <MapPin size={12} />
          {record.location_name}
        </span>
      )}
      <span className="inline-flex items-center gap-1">
        <FileCheck size={12} />
        {TRUST_LEVEL_COPY[record.verification_level].label}
      </span>
    </div>
  );
}
